import * as pc from 'playcanvas';
import {EventBus,ExperienceRuntime} from './runtime.js';

class PlayCanvasAdapter{
  constructor(app,events){
    this.app=app;
    this.events=events;
    this.objects=new Map();
    this.states=new Map();
    this.zones=new Map();
    this.inside=new Set();
    this.camera=null;
    this.runtime=null;
    this.assetManifest=null;
  }

  async loadScene(scene){
    this.app.scene.ambientLight=new pc.Color(.28,.3,.34);

    this.camera=new pc.Entity('PlayerCamera');
    this.camera.addComponent('camera',{clearColor:new pc.Color(.03,.04,.06)});
    this.camera.setPosition(0,1.65,1.2);
    this.camera.lookAt(0,1,-3);
    this.app.root.addChild(this.camera);

    const l=new pc.Entity('Light');
    l.addComponent('light',{type:'directional',intensity:1.8});
    l.setEulerAngles(50,-35,0);
    this.app.root.addChild(l);

    const f=new pc.Entity('Floor');
    f.addComponent('render',{type:'box'});
    f.setPosition(0,-.05,-3);
    f.setLocalScale(10,.1,14);
    this.app.root.addChild(f);

    this.buildResolvedEnvironment();

    for(const d of scene.objects??[]) this.createObject(d);
    for(const z of scene.zones??[]) this.createZone(z);

    this.app.on('update',dt=>this.update(dt));
  }

  createObject(d){
    const e=new pc.Entity(d.id);
    e.addComponent('render',{type:d.primitive||'box'});
    e.setPosition(...d.position);
    e.setLocalScale(...d.scale);

    const m=new pc.StandardMaterial();
    m.diffuse=color(d.color);
    m.update();
    e.render.material=m;

    this.app.root.addChild(e);
    this.objects.set(d.id,e);
    this.states.set(d.id,{...(d.state??{})});

    // Replace primitive fallback with resolved GLB when available.
    this.tryLoadResolvedModel(d,e);
  }

  createZone(z){
    this.zones.set(z.id,z);

    if(z.debugVisible){
      const e=new pc.Entity('zone_'+z.id);
      e.addComponent('render',{type:'box'});
      e.setPosition(...z.position);
      e.setLocalScale(...z.size);

      const m=new pc.StandardMaterial();
      m.diffuse=new pc.Color(.08,.5,.9);
      m.opacity=.16;
      m.blendType=pc.BLEND_NORMAL;
      m.update();

      e.render.material=m;
      this.app.root.addChild(e);
    }
  }






  tryLoadResolvedModel(def,placeholder){
    const manifest=this.assetManifest?.assets??[];
    const resolved=manifest.find(a=>
      a.usage===def.id &&
      a.status==='resolved' &&
      a.resolvedUrl &&
      (a.type==='model3d' || a.type==='character')
    );

    if(!resolved){
      this.reportAsset(def.id,'fallback','primitive');
      return;
    }

    const absoluteUrl=new URL(resolved.resolvedUrl,window.location.origin).href;
    this.reportAsset(def.id,'loading','requesting GLB');

    let finished=false;
    const timeout=setTimeout(()=>{
      if(finished)return;
      this.reportAsset(def.id,'timeout','GLB load > 25s; primitive kept');
    },25000);

    // Single download only. PlayCanvas handles fetch + parse.
    this.app.assets.loadFromUrl(absoluteUrl,'container',(err,asset)=>{
      finished=true;
      clearTimeout(timeout);

      if(err || !asset?.resource){
        console.error('GLB load failed',def.id,absoluteUrl,err);
        this.reportAsset(def.id,'error',String(err||'container resource missing'));
        return;
      }

      try{
        const model=asset.resource.instantiateRenderEntity({
          castShadows:true
        });

        model.name=`resolved_${def.id}`;
        this.app.root.addChild(model);
        model.setPosition(def.position[0],def.position[1],def.position[2]);
        model.setLocalEulerAngles(0,0,0);
        model.setLocalScale(1,1,1);

        let bounds=this.computeHierarchyBounds(model);
        if(!bounds) throw new Error('GLB contains no render mesh');

        const size=bounds.halfExtents.clone().mulScalar(2);
        const target=this.targetDimensions(def);

        const ratios=[
          target.x/Math.max(size.x,.001),
          target.y/Math.max(size.y,.001),
          target.z/Math.max(size.z,.001)
        ].filter(v=>Number.isFinite(v)&&v>0);

        let uniform=ratios.length ? Math.min(...ratios) : 1;
        uniform=Math.max(.02,Math.min(20,uniform));

        model.setLocalScale(uniform,uniform,uniform);
        model.syncHierarchy?.();

        bounds=this.computeHierarchyBounds(model);
        if(bounds){
          const desiredBottom=this.targetBottom(def,target);
          const actualBottom=bounds.center.y-bounds.halfExtents.y;
          model.translate(0,desiredBottom-actualBottom,0);
        }

        placeholder.enabled=false;
        this.objects.set(def.id,model);

        this.reportAsset(
          def.id,
          'loaded',
          `scale=${uniform.toFixed(3)}`
        );

      }catch(e){
        console.error('GLB instantiate failed',def.id,e);
        this.reportAsset(def.id,'error',String(e));
      }
    });
  }


  targetDimensions(def){
    // The old schema primitive scale was only a debug proxy size.
    // Real assets use semantic defaults in meters so generated models remain visible.
    if(def.type==='vehicle' || def.id.includes('scooter')){
      return new pc.Vec3(.55,1.25,1.15);
    }
    if(def.type==='signal' || def.id.includes('traffic_light')){
      return new pc.Vec3(.55,2.8,.55);
    }
    if(def.type==='npc' || def.id.includes('pedestrian')){
      return new pc.Vec3(.65,1.75,.65);
    }
    return new pc.Vec3(
      Math.max(.3,def.scale?.[0]??1),
      Math.max(.3,def.scale?.[1]??1),
      Math.max(.3,def.scale?.[2]??1)
    );
  }

  targetBottom(def,target){
    // Most schema positions are authored near the object's centre.
    // For semantic assets, put them on the ground plane.
    if(
      def.type==='vehicle' || def.type==='signal' || def.type==='npc' ||
      def.id.includes('scooter') || def.id.includes('traffic_light') ||
      def.id.includes('pedestrian')
    ){
      return 0;
    }
    return (def.position?.[1]??target.y/2)-target.y/2;
  }


  computeHierarchyBounds(root){
    const meshInstances=[];
    const walk=(e)=>{
      if(e.render?.meshInstances) meshInstances.push(...e.render.meshInstances);
      for(const c of e.children??[]) walk(c);
    };
    walk(root);
    if(!meshInstances.length) return null;

    let bounds=meshInstances[0].aabb.clone();
    for(let i=1;i<meshInstances.length;i++) bounds.add(meshInstances[i].aabb);
    return bounds;
  }

  reportAsset(id,status,message){
    window.dispatchEvent(new CustomEvent('xr-asset-status',{
      detail:{id,status,message}
    }));
  }

  buildResolvedEnvironment(){
    const assets=this.assetManifest?.assets??[];
    const urban=assets.find(a=>
      a.id==='env_urban_street' &&
      a.status==='resolved' &&
      a.resolution?.metadata?.renderer==='procedural_urban_street'
    );
    if(!urban) return;

    const road=new pc.Entity('ResolvedRoad');
    road.addComponent('render',{type:'box'});
    road.setPosition(0,.01,-6);
    road.setLocalScale(6,.04,16);
    const rm=new pc.StandardMaterial();
    rm.diffuse=new pc.Color(.12,.13,.14);
    rm.update();
    road.render.material=rm;
    this.app.root.addChild(road);

    for(const x of [-3.8,3.8]){
      const sidewalk=new pc.Entity('Sidewalk');
      sidewalk.addComponent('render',{type:'box'});
      sidewalk.setPosition(x,.08,-6);
      sidewalk.setLocalScale(1.5,.16,16);
      const sm=new pc.StandardMaterial();
      sm.diffuse=new pc.Color(.42,.42,.4);
      sm.update();
      sidewalk.render.material=sm;
      this.app.root.addChild(sidewalk);
    }

    for(let x=-2.5;x<=2.5;x+=1){
      const stripe=new pc.Entity('CrosswalkStripe');
      stripe.addComponent('render',{type:'box'});
      stripe.setPosition(x,.045,-5.2);
      stripe.setLocalScale(.55,.02,1.7);
      const wm=new pc.StandardMaterial();
      wm.diffuse=new pc.Color(.9,.9,.88);
      wm.update();
      stripe.render.material=wm;
      this.app.root.addChild(stripe);
    }
  }

  hasObject(id){
    return this.objects.has(id);
  }

  setObjectVisible(id,v){
    const e=this.objects.get(id);
    if(e) e.enabled=v;
  }

  getObjectState(id,key){
    return this.states.get(id)?.[key];
  }

  setObjectState(id,key,value){
    const s=this.states.get(id)??{};
    s[key]=value;
    this.states.set(id,s);
  }

  isPlayerInZone(id){
    return this.inside.has(id);
  }

  update(dt){
    if(!this.app.xr?.active) this.desktopMove(dt);

    if(!this.camera) return;

    const p=this.camera.getPosition();

    for(const [id,z] of this.zones){
      const now=inBox(p,z.position,z.size);
      const was=this.inside.has(id);

      if(now && !was){
        this.inside.add(id);
        this.events.emit('player.enter_zone',{
          target:id,
          position:[p.x,p.y,p.z]
        });
      } else if(!now && was){
        this.inside.delete(id);
        this.events.emit('player.exit_zone',{
          target:id,
          position:[p.x,p.y,p.z]
        });
      }
    }
  }

  desktopMove(dt){
    const kb=this.app.keyboard;
    if(!kb || !this.camera) return;

    const speed=2.4*dt;
    const d=new pc.Vec3();

    if(kb.isPressed(pc.KEY_W)) d.z-=speed;
    if(kb.isPressed(pc.KEY_S)) d.z+=speed;
    if(kb.isPressed(pc.KEY_A)) d.x-=speed;
    if(kb.isPressed(pc.KEY_D)) d.x+=speed;

    if(d.lengthSq()>0) this.camera.translate(d);
  }

  enterXR(){
    if(!this.app.xr?.isAvailable(pc.XRTYPE_VR)){
      document.getElementById('status').textContent='WebXR VR no disponible.';
      return;
    }

    this.app.xr.start(
      this.camera.camera,
      pc.XRTYPE_VR,
      pc.XRSPACE_LOCALFLOOR,
      {
        callback:e=>{
          if(e) console.error(e);
        }
      }
    );

    this.app.xr.input.on('select',src=>{
      const t=this.runtime?.currentStep?.trigger?.target;
      this.events.emit('button.pressed',{
        target:t??'controller',
        source:src
      });
    });

    this.app.xr.input.on('squeeze',src=>{
      const t=this.runtime?.currentStep?.trigger?.target;
      this.events.emit('object.grabbed',{
        target:t??'object',
        source:src
      });
    });
  }
}

function inBox(p,pos,size){
  return Math.abs(p.x-pos[0])<=size[0]/2
      && Math.abs(p.y-pos[1])<=size[1]/2
      && Math.abs(p.z-pos[2])<=size[2]/2;
}

function color(n){
  const c={
    red:[.85,.08,.08],
    blue:[.08,.3,.9],
    orange:[1,.28,.02],
    green:[.08,.75,.25],
    yellow:[.9,.75,.08],
    purple:[.55,.2,.85],
    gray:[.5,.5,.5]
  };
  return new pc.Color(...(c[n]||c.gray));
}

function demo(){
  return {
    schemaVersion:'0.4.1',
    id:'demo',
    title:'XR Semantic Runtime Demo',
    description:'demo',
    category:'other',
    estimatedDurationSeconds:120,
    scene:{
      environment:'basic_room',
      objects:[
        {
          id:'focus',
          type:'prop',
          primitive:'box',
          position:[0,1,-4],
          scale:[1,1,1],
          color:'blue',
          state:{active:true}
        }
      ],
      zones:[
        {
          id:'focus_zone',
          position:[0,1,-2.5],
          size:[4,3,2],
          debugVisible:true
        }
      ]
    },
    variables:{score:0},
    steps:[
      {
        id:'zone',
        title:'Zona',
        objective:'Camina con WASD hasta la zona azul.',
        trigger:{event:'player.enter_zone',target:'focus_zone'},
        conditions:[],
        successActions:[
          {type:'score.add',value:50,key:null,objectId:null,timerId:null,durationMs:null},
          {type:'step.next',value:null,key:null,objectId:null,timerId:null,durationMs:null}
        ],
        failActions:[],
        policy:{cooldownMs:1000,maxFailures:null,consumeOnSuccess:true,consumeOnFailure:false}
      },
      {
        id:'finish',
        title:'Final',
        objective:'Pulsa ACTION.',
        trigger:{event:'button.pressed',target:'focus'},
        conditions:[],
        successActions:[
          {type:'score.add',value:50,key:null,objectId:null,timerId:null,durationMs:null},
          {type:'experience.complete',value:null,key:null,objectId:null,timerId:null,durationMs:null}
        ],
        failActions:[],
        policy:{cooldownMs:500,maxFailures:null,consumeOnSuccess:true,consumeOnFailure:false}
      }
    ]
  };
}

function logDecision(d){
  const box=document.getElementById('decisions');
  const row=document.createElement('div');
  row.className='decision '+(
    d.outcome==='success' ? 'ok'
    : d.outcome==='failure' ? 'bad'
    : 'ignored'
  );
  row.textContent=
    `${d.stepId} · ${d.event} · ${d.outcome} · ${d.reason} · score=${d.score}`;
  box.prepend(row);
}

function updateUI(schema,s){
  document.getElementById('title').textContent=schema.title;
  document.getElementById('objective').innerHTML=
    s.step ? `<b>${s.step.title}</b><br>${s.step.objective}` : '';

  document.getElementById('score').textContent=
    `Score: ${s.state.variables.score??0}`;

  document.getElementById('status').textContent=
    `${s.type} · speed=${Number(s.state.variables.vehicleSpeed??0).toFixed(2)}`;

  const fb=document.getElementById('feedback');
  fb.style.display=s.state.feedback ? 'block' : 'none';
  fb.textContent=s.state.feedback??'';

  if(s.state.completed){
    document.getElementById('objective').innerHTML=
      '<b>Experiencia completada ✓</b>';
  }
}

async function loadSchema(){
  const qs=new URLSearchParams(location.search);
  const experienceId=qs.get('experience');

  if(!experienceId) return demo();

  const r=await fetch(
    `/api/experiences/${encodeURIComponent(experienceId)}`
  );

  if(!r.ok){
    throw new Error(`Experience not found (${r.status})`);
  }

  return await r.json();
}

async function boot(){
  try{
    const schema=await loadSchema();

    const host=document.getElementById('app');
    const canvas=document.createElement('canvas');
    host.appendChild(canvas);

    const app=new pc.Application(canvas,{
      keyboard:new pc.Keyboard(window)
    });

    app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    app.setCanvasResolution(pc.RESOLUTION_AUTO);
    app.start();

    const events=new EventBus();
    const adapter=new PlayCanvasAdapter(app,events);

    try{
      if(schema.id && schema.id!=='demo'){
        const rr=await fetch(`/api/experiences/${encodeURIComponent(schema.id)}/assets/resolve`,{
          method:'POST'
        });
        if(rr.ok){
          adapter.assetManifest=await rr.json();
          console.info('XR Asset Manifest',adapter.assetManifest);
        }
      }
    }catch(assetError){
      console.warn('Asset resolver unavailable; using primitives.',assetError);
    }

    let runtime;

    runtime=new ExperienceRuntime({
      schema,
      adapter,
      events,
      onState:s=>updateUI(schema,s),
      onDecision:logDecision
    });

    adapter.runtime=runtime;

    await runtime.start();

    document.getElementById('enterXR').onclick=()=>{
      adapter.enterXR();
    };

    document.getElementById('brake').onclick=()=>{
      const v=Math.max(
        0,
        (Number(runtime.state.variables.vehicleSpeed)||0)-1.25
      );
      runtime.setVariable('vehicleSpeed',v);
      events.emit('vehicle.brake',{
        target:'scooter',
        speed:v
      });
    };

    document.getElementById('accelerate').onclick=()=>{
      const v=
        (Number(runtime.state.variables.vehicleSpeed)||0)+.75;

      runtime.setVariable('vehicleSpeed',v);
      events.emit('vehicle.accelerate',{
        target:'scooter',
        speed:v
      });
    };

    document.getElementById('action').onclick=()=>{
      const t=runtime.currentStep?.trigger;
      if(!t) return;

      if(t.event==='vehicle.brake'){
        document.getElementById('brake').click();
        return;
      }

      events.emit(t.event,{
        target:t.target
      });
    };

    window.addEventListener('resize',()=>{
      app.resizeCanvas();
    });

  }catch(err){
    console.error(err);
    document.getElementById('objective').textContent=
      `Error cargando experiencia: ${err.message}`;
    document.getElementById('status').textContent=
      'Runtime initialization failed';
  }
}


window.addEventListener('xr-asset-status',(ev)=>{
  const box=document.getElementById('assetDiag');
  if(!box)return;
  const d=ev.detail;
  let row=document.getElementById('asset_'+d.id);
  if(!row){
    row=document.createElement('div');
    row.id='asset_'+d.id;
    box.appendChild(row);
  }
  row.textContent=`${d.id}: ${d.status} — ${d.message}`;
});

boot();
